import {
  Button,
  Dialog,
  Divider,
  InputBase,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { withStyles } from 'tss-react/mui';
import { chain, sortBy } from 'lodash';
import { connect } from 'redux-bundler-react';
import BusinessCenterIcon from '@material-ui/icons/BusinessCenter';
import CakeIcon from '@material-ui/icons/Cake';
import DesktopWindowsIcon from '@material-ui/icons/DesktopWindows';
import LocationCityIcon from '@material-ui/icons/LocationCity';
import React from 'react';
import SettingsIcon from '@material-ui/icons/Settings';
import matchSorter from 'match-sorter';
import pluralize from 'pluralize';

import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

const styles = theme => ({
  categories: {
    padding: theme.spacing(1.5),
    display: 'flex',
    justifyContent: 'start',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  empty: {
    margin: 'auto',
    height: '100',
    display: 'flex',
    alignItems: 'center',
  },
  footer: {
    borderTop: `1px solid rgba(0, 0, 0, 0.23)`,
    padding: theme.spacing(1.5),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  form: {},
  input: {
    border: 'none',
    padding: theme.spacing(2),
  },
  topScrollPaper: {
    alignItems: 'flex-start',
  },
  topPaperScrollBody: {
    verticalAlign: 'top',
  },
  list: {
    maxHeight: 'calc(90vh - 64px)',
    overflowY: 'scroll',
  },
  chip: {
    borderRadius: 8,
  },
  right: {
    textAlign: 'right',
  },
  key: {
    border: `1px solid rgba(0, 0, 0, 0.23)`,
    fontFamily:
      "source-code-pro, Menlo, Monaco, Consolas, 'Courier New',    monospace",
    backgroundColor: theme.palette.background.paper,
    fontSize: '12px',
    borderRadius: 4,
    paddingLeft: 4,
    paddingRight: 4,
  },
  icon: {
    minWidth: theme.spacing(4),
  },
  subheader: {
    // backgroundColor: darken(theme.palette.background.paper, 0.06),
  },
  divider: {
    marginTop: theme.spacing(1),
    '&:last-child': {
      display: 'none',
    },
  },

  cancel: {
    textAlign: 'right',

    borderBottom: `1px solid rgba(0, 0, 0, 0.23)`,
  },

  additionalData: {
    display: 'flex',
    verticalAlign: 'middle',
  },

  additionalDataWrapper: {
    display: 'flex',
    verticalAlign: 'middle',
    gap: theme.spacing(1),
  },
});

const isSearchable = item => {
  return ['Facility', 'Client'].includes(item.type);
};
const ICONS = {
  Facility: LocationCityIcon,
  Workstation: DesktopWindowsIcon,
  ImagePipeline: CakeIcon,
  Client: BusinessCenterIcon,
};

const RecentItemFilterButton = withStyles(
  ({ name, selectedCategory, classes, setSelectedCategory, inputRef }) => {
    return (
      <Button
        disableElevation
        variant={selectedCategory === name ? 'contained' : 'outlined'}
        color="primary"
        size="small"
        onClick={() => {
          setSelectedCategory(name);
          inputRef.current.focus();
        }}
      >
        {name}
      </Button>
    );
  },
  styles,
  { withTheme: true },
);

const CATEGORIES = [
  'All',
  'Client',
  'Facility',
  'ImagePipeline',
  'Workstation',
];
const AdditionalData = connect(
  'selectImagePipelineRecentItems',
  withStyles(
    ({ item, imagePipelineRecentItems, classes }) => {
      const Icon = ICONS[item.type];
      const defaultResult = (
        <>
          <ListItemIcon className={classes.icon}>
            {Icon && <Icon />}
          </ListItemIcon>
          <ListItemText primary={item.name} />
        </>
      );

      if (item.type !== 'ImagePipeline') {
        return defaultResult;
      }

      const match = imagePipelineRecentItems.find(
        x => x.id == item.imagePipelineId,
      );

      if (!match) {
        return defaultResult;
      }
      if (match && !match.lastImagePipelineSync) {
        return defaultResult;
      }

      return (
        <>
          <ListItemIcon className={classes.icon}>
            {match.lastImagePipelineSync.isFailed() && (
              <ErrorOutlineOutlinedIcon
                fontSize="12px"
                className={classes.icon}
                style={{ color: '#f83245' }}
              />
            )}{' '}
            {match.lastImagePipelineSync.isComplete() && (
              <CheckCircleOutlineOutlinedIcon
                fontSize="12px"
                className={classes.icon}
                style={{ color: '#1bc943' }}
              />
            )}{' '}
            {match.lastImagePipelineSync.isCancelling() && (
              <ErrorOutlineOutlinedIcon
                fontSize="12px"
                className={classes.icon}
                color="secondary"
              />
            )}{' '}
            {match.lastImagePipelineSync.isInProgress() && (
              <PlayArrowIcon
                fontSize="12px"
                className={classes.icon}
                color="primary"
              />
            )}
          </ListItemIcon>
          <ListItemText
            primary={
              <div className={classes.additionalDataWrapper}>
                <div className={classes.additionalTitle}>{item.name}</div>{' '}
                <Typography variant="caption">
                  #{match.lastImagePipelineSync.buildNumber}{' '}
                  {match.lastImagePipelineSync.state}
                </Typography>
              </div>
            }
          />
        </>
      );
    },
    styles,
    { withTheme: true },
  ),
);

const Recents = ({
  open,
  classes,
  onClose,
  recentItems,
  doUpdateUrl,
  doClearRecentItems,
  onSetClient,
  onSetFacility,
  imagePipelineRecentItems,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [query, setQuery] = React.useState('');
  const inputEl = React.useRef(null);
  const [selected, setSelected] = React.useState(0);
  const [selectedUrl, setSelectedUrl] = React.useState(null);
  const [selectedCategory, setSelectedCategory] = React.useState(CATEGORIES[0]);

  const [filteredRecentItems, setFilteredRecentItems] = React.useState([]);
  const handleKeyDown = e => {
    // console.log(filteredRecentItems[selected].type);
    if (e.key === 'Enter' || e.key === 'Tab') {
      if (open && filteredRecentItems[selected]) {
        if (e.metaKey || e.ctrlKey) {
          window.open(filteredRecentItems[selected].url, '_blank');
        } else {
          if (
            e.key === 'Tab' &&
            filteredRecentItems[selected].type === 'Client'
          ) {
            onSetClient(filteredRecentItems[selected]);
          } else if (
            e.key === 'Tab' &&
            filteredRecentItems[selected].type === 'Facility'
          ) {
            onSetFacility(filteredRecentItems[selected]);
          } else {
            doUpdateUrl(filteredRecentItems[selected].url);
          }
        }
        onClose();
      }
      e.preventDefault();
    }
    if (e.key === 'ArrowDown') {
      if (open && filteredRecentItems) {
        let nextSelected;
        if (filteredRecentItems.length - 1 > selected) {
          nextSelected = selected + 1;
        } else {
          nextSelected = 0;
        }
        setSelected(nextSelected);
        setSelectedUrl(filteredRecentItems[nextSelected].url);
      }
      e.preventDefault();
    }

    if (e.key === 'ArrowUp') {
      if (open && filteredRecentItems) {
        let nextSelected;
        if (selected > 0) {
          nextSelected = selected - 1;
        } else {
          nextSelected = filteredRecentItems.length - 1;
        }
        setSelected(nextSelected);
        setSelectedUrl(filteredRecentItems[nextSelected].url);
      }
      e.preventDefault();
    }

    if (e.key === 'ArrowRight') {
      if (query !== '') {
        return;
      }
      const nextCategoryIndex = CATEGORIES.indexOf(selectedCategory) + 1;
      if (nextCategoryIndex < CATEGORIES.length) {
        setSelectedCategory(CATEGORIES[nextCategoryIndex]);
      } else {
        setSelectedCategory(CATEGORIES[0]);
      }

      e.preventDefault();
    }

    if (e.key === 'ArrowLeft') {
      if (query !== '') {
        return;
      }

      const nextCategoryIndex = CATEGORIES.indexOf(selectedCategory) - 1;

      if (nextCategoryIndex >= 0) {
        setSelectedCategory(CATEGORIES[nextCategoryIndex]);
      } else {
        setSelectedCategory(CATEGORIES[CATEGORIES.length - 1]);
      }

      e.preventDefault();
    }
  };

  const listItemRefCallback = React.useCallback(
    ref => {
      if (!ref) return;

      ref.scrollIntoView(false);
    },
    [selected],
  );

  React.useEffect(() => {
    let filteredItemsBeforeSorting;
    if (query !== '') {
      filteredItemsBeforeSorting = matchSorter(recentItems, query.trim(), {
        keys: ['name'],
      });
    } else {
      filteredItemsBeforeSorting = recentItems;
    }

    if (selectedCategory !== 'All') {
      filteredItemsBeforeSorting = filteredItemsBeforeSorting.filter(
        i => i.type == selectedCategory,
      );
    }

    const filteredItemsAfterSorting = sortBy(filteredItemsBeforeSorting, a => [
      a.type,
      a.name,
    ]);
    setFilteredRecentItems(filteredItemsAfterSorting);

    setSelected(0);
    setSelectedUrl(filteredItemsAfterSorting[0]?.url);
  }, [query, recentItems, selectedCategory]);

  // console.log(imagePipelineRecentItems);
  return (
    <Dialog
      maxWidth="md"
      fullWidth
      aria-labelledby="confirmation-dialog-title"
      open={open}
      fullScreen={fullScreen}
      onClose={onClose}
      classes={{
        scrollPaper: classes.topScrollPaper,
        paperScrollBody: classes.topPaperScrollBody,
      }}
    >
      {fullScreen && (
        <div className={classes.cancel} onClick={() => onClose()}>
          <Button>Cancel</Button>
        </div>
      )}
      <div className={classes.form} onKeyDown={handleKeyDown}>
        <InputBase
          inputRef={inputEl}
          className={classes.input}
          value={query}
          onChange={e => setQuery(e.target.value)}
          autoFocus
          fullWidth
          variant="outlined"
          placeholder={'Recent items'}
          /* onKeyDown={handleKeyDown} */
        />
      </div>

      <Divider />

      <div className={classes.categories}>
        {CATEGORIES.map(category => (
          <RecentItemFilterButton
            name={category}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            inputRef={inputEl}
          />
        ))}
      </div>

      <Divider />
      {recentItems.length === 0 && (
        <div className={classes.empty}>No recent items to show</div>
      )}

      {filteredRecentItems.length === 0 && recentItems.length > 0 && (
        <div className={classes.empty}>No recent items match your search</div>
      )}
      {filteredRecentItems.length > 0 && (
        <List dense className={classes.list}>
          {chain(filteredRecentItems)
            .groupBy(d => d.type)
            .map((items, type, index) => (
              <>
                <ListSubheader className={classes.subheader} disableSticky>
                  {pluralize(type)}
                </ListSubheader>
                {items.map(item => {
                  const Icon = ICONS[item.type];
                  return (
                    <ListItem
                      button
                      component="a"
                      href={item.url}
                      ref={
                        item.url === selectedUrl ? listItemRefCallback : null
                      }
                      selected={item.url === selectedUrl}
                      onClick={() => {
                        /* doUpdateUrl(item.url); */
                        onClose();
                      }}
                    >
                      <AdditionalData item={item} />
                      <ListItemText
                        secondary={item.secondary}
                        className={classes.right}
                      />
                    </ListItem>
                  );
                })}

                <Divider className={classes.divider} />
              </>
            ))
            .value()}
        </List>
      )}
      <div className={classes.footer}>
        <Typography variant="caption" color="textSecondary">
          <kbd className={classes.key}>ctrl+k</kbd> Search for Facilities,
          Workstations, Templates etc
        </Typography>

        <SettingsIcon
          htmlColor="grey"
          onClick={event => setAnchorEl(event.currentTarget)}
        />

        <Menu
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          className={classes.menu}
        >
          <MenuItem
            className={classes.menuItem}
            onClick={() => {
              setAnchorEl(null);
              doClearRecentItems();
            }}
          >
            Clear history
          </MenuItem>
        </Menu>
      </div>
    </Dialog>
  );
};

export default connect(
  'doUpdateUrl',
  'selectActiveClient',

  'selectClientData',
  'selectRecentItems',
  'selectImagePipelineRecentItems',
  'doClearRecentItems',
  withStyles(Recents, styles, { withTheme: true }),
);
